import React, { useState, useMemo } from 'react';
import {
  ComposedChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
} from 'recharts';
import { compactCurrency, compactCurrencyAxis, currencyFormatter } from '../utils/reportingUtils';
import { Skeleton } from '@opendate-app/setlist';

const EVENT_A_COLOR = 'var(--sl-fg-brand, #1982C4)';
const EVENT_B_COLOR = '#FAB714';

const CompareEventsTooltip = ({ active, payload, metric, eventAName, eventBName }) => {
  if (!active || !payload || !payload.length) return null;
  const point = payload[0]?.payload;
  if (!point) return null;

  const formatValue = (name) => {
    if (metric === 'revenue') {
      return currencyFormatter.format(point[name] || 0);
    }
    return (point[`${name}_count`] || 0).toLocaleString();
  };

  return (
    <div
      style={{
        backgroundColor: 'var(--sl-bg-primary, #fff)',
        border: '1px solid var(--sl-border-secondary, #e9eaeb)',
        borderRadius: 8,
        padding: '10px 14px',
        boxShadow: '0px 4px 6px -2px rgba(10,13,18,0.03), 0px 12px 16px -4px rgba(10,13,18,0.08)',
        fontSize: 13,
        lineHeight: '20px',
      }}
    >
      <div style={{ fontWeight: 600, marginBottom: 4, color: 'var(--sl-text-secondary, #333)' }}>
        {point.month}
      </div>
      <div style={{ color: EVENT_A_COLOR }}>
        {eventAName}: {formatValue(eventAName)}
      </div>
      {eventBName && (
        <div style={{ color: EVENT_B_COLOR }}>
          {eventBName}: {formatValue(eventBName)}
        </div>
      )}
    </div>
  );
};

const ComparisonStatCard = ({ label, valueA, valueB, nameA, nameB }) => (
  <div
    style={{
      flex: 1,
      padding: '10px 14px',
      backgroundColor: 'var(--sl-bg-secondary, #fafafa)',
      borderRadius: 8,
      border: '1px solid var(--sl-border-secondary, #e9eaeb)',
      minWidth: 0,
    }}
  >
    <div
      style={{
        fontSize: 11,
        fontWeight: 500,
        color: 'var(--sl-text-quaternary, #717680)',
        marginBottom: 6,
        textTransform: 'uppercase',
        letterSpacing: '0.03em',
      }}
    >
      {label}
    </div>
    <div style={{ display: 'flex', alignItems: 'baseline', gap: 6, marginBottom: nameB ? 2 : 0 }}>
      <span style={{ fontSize: 15, fontWeight: 600, color: EVENT_A_COLOR }}>{valueA}</span>
      <span style={{ fontSize: 11, color: 'var(--sl-text-quaternary, #717680)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{nameA}</span>
    </div>
    {nameB && (
      <div style={{ display: 'flex', alignItems: 'baseline', gap: 6 }}>
        <span style={{ fontSize: 15, fontWeight: 600, color: EVENT_B_COLOR }}>{valueB}</span>
        <span style={{ fontSize: 11, color: 'var(--sl-text-quaternary, #717680)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{nameB}</span>
      </div>
    )}
  </div>
);

const LegendItem = ({ color, label }) => (
  <div style={{ display: 'flex', alignItems: 'center', gap: 6, minWidth: 0 }}>
    <div
      style={{
        width: 16,
        height: 3,
        backgroundColor: color,
        borderRadius: 2,
        flexShrink: 0,
      }}
    />
    <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{label}</span>
  </div>
);

const CompareEventsChart = ({ data, loading }) => {
  const [metric, setMetric] = useState('revenue');

  const eventA = data?.event_a;
  const eventB = data?.event_b;
  const chartData = data?.chart || [];

  const eventAName = eventA?.name || 'Event A';
  const eventBName = eventB?.name || null;

  const dataKeyA = metric === 'revenue' ? eventAName : `${eventAName}_count`;
  const dataKeyB = eventBName ? (metric === 'revenue' ? eventBName : `${eventBName}_count`) : null;

  const tickInterval = useMemo(
    () => Math.max(1, Math.floor(chartData.length / 8)),
    [chartData.length]
  );

  const yTickFormatter = metric === 'revenue'
    ? compactCurrencyAxis
    : (v) => v.toLocaleString();

  if (loading) {
    return (
      <div
        style={{
          backgroundColor: 'var(--sl-bg-primary, #fff)',
          border: '1px solid var(--sl-border-secondary, #e9eaeb)',
          borderRadius: 12,
          boxShadow: '0px 1px 2px 0px rgba(10,13,18,0.05)',
          marginBottom: 20,
          padding: 24,
        }}
      >
        <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 16 }}>
          <Skeleton width="30%" height={20} />
          <Skeleton width="140px" height={32} />
        </div>
        <div style={{ display: 'flex', gap: 16, marginBottom: 20 }}>
          {[1, 2, 3, 4].map((i) => (
            <div key={i} style={{ flex: 1 }}>
              <Skeleton width="100%" height={72} radius={8} />
            </div>
          ))}
        </div>
        <Skeleton width="100%" height={320} radius={8} />
      </div>
    );
  }

  if (!eventA) return null;

  return (
    <div
      style={{
        backgroundColor: 'var(--sl-bg-primary, #fff)',
        border: '1px solid var(--sl-border-secondary, #e9eaeb)',
        borderRadius: 12,
        boxShadow: '0px 1px 2px 0px rgba(10,13,18,0.05)',
        marginBottom: 20,
        padding: 24,
      }}
    >
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          marginBottom: 16,
        }}
      >
        <span style={{ fontSize: 16, fontWeight: 600, color: 'var(--sl-text-secondary, #333)' }}>
          Cumulative Sales
        </span>
        <div
          style={{
            display: 'flex',
            backgroundColor: 'var(--sl-bg-secondary, #fafafa)',
            border: '1px solid var(--sl-border-secondary, #e9eaeb)',
            borderRadius: 10,
            padding: 4,
            gap: 4,
          }}
        >
          {['revenue', 'tickets'].map((v) => (
            <button
              key={v}
              onClick={() => setMetric(v)}
              style={{
                padding: '4px 10px',
                border: 'none',
                borderRadius: 6,
                background: metric === v ? 'var(--sl-bg-primary, #fff)' : 'transparent',
                boxShadow:
                  metric === v
                    ? '0px 1px 3px 0px rgba(10,13,18,0.1), 0px 1px 2px -1px rgba(10,13,18,0.1)'
                    : 'none',
                cursor: 'pointer',
                fontWeight: 600,
                color: metric === v ? 'var(--sl-text-secondary, #414651)' : 'var(--sl-text-quaternary, #717680)',
                fontSize: 13,
                lineHeight: '20px',
                textTransform: 'capitalize',
              }}
            >
              {v}
            </button>
          ))}
        </div>
      </div>

      <div style={{ display: 'flex', gap: 16, marginBottom: 20 }}>
        <ComparisonStatCard
          label="Venue Gross"
          valueA={compactCurrency(eventA.venue_gross)}
          valueB={eventB ? compactCurrency(eventB.venue_gross) : null}
          nameA={eventAName}
          nameB={eventBName}
        />
        <ComparisonStatCard
          label="Tickets Sold"
          valueA={(eventA.tickets_sold || 0).toLocaleString()}
          valueB={eventB ? (eventB.tickets_sold || 0).toLocaleString() : null}
          nameA={eventAName}
          nameB={eventBName}
        />
        <ComparisonStatCard
          label="Capacity %"
          valueA={`${eventA.capacity_pct}%`}
          valueB={eventB ? `${eventB.capacity_pct}%` : null}
          nameA={eventAName}
          nameB={eventBName}
        />
        <ComparisonStatCard
          label="Ticket Buyers"
          valueA={(eventA.ticket_buyers || 0).toLocaleString()}
          valueB={eventB ? (eventB.ticket_buyers || 0).toLocaleString() : null}
          nameA={eventAName}
          nameB={eventBName}
        />
      </div>

      <div
        style={{
          display: 'flex',
          gap: 20,
          marginBottom: 12,
          fontSize: 12,
          color: 'var(--sl-text-quaternary, #717680)',
        }}
      >
        <LegendItem color={EVENT_A_COLOR} label={eventAName} />
        {eventBName && <LegendItem color={EVENT_B_COLOR} label={eventBName} />}
      </div>

      {chartData.length === 0 ? (
        <div
          style={{
            textAlign: 'center',
            padding: '40px 0',
            color: 'var(--sl-text-tertiary, #667085)',
            fontSize: 14,
          }}
        >
          No sales timeline data available. Events may not have on-sale dates configured.
        </div>
      ) : (
        <ResponsiveContainer width="100%" height={320}>
          <ComposedChart data={chartData} margin={{ top: 5, right: 20, bottom: 5, left: 10 }}>
            <CartesianGrid
              strokeDasharray="3 3"
              stroke="#f1f1f1"
              vertical={false}
            />
            <XAxis
              dataKey="month"
              tick={{ fontSize: 11, fill: 'var(--sl-text-quaternary, #717680)' }}
              tickLine={false}
              axisLine={{ stroke: 'var(--sl-border-secondary, #e9eaeb)' }}
              interval={tickInterval}
            />
            <YAxis
              tick={{ fontSize: 11, fill: 'var(--sl-text-quaternary, #717680)' }}
              tickLine={false}
              axisLine={false}
              tickFormatter={yTickFormatter}
              width={55}
            />
            <Tooltip
              content={
                <CompareEventsTooltip
                  metric={metric}
                  eventAName={eventAName}
                  eventBName={eventBName}
                />
              }
              cursor={{ stroke: 'var(--sl-border-primary, #d0d5dd)', strokeDasharray: '3 3' }}
            />
            <Line
              type="monotone"
              dataKey={dataKeyA}
              stroke={EVENT_A_COLOR}
              strokeWidth={2.5}
              dot={false}
              isAnimationActive={false}
              name={eventAName}
            />
            {dataKeyB && (
              <Line
                type="monotone"
                dataKey={dataKeyB}
                stroke={EVENT_B_COLOR}
                strokeWidth={2.5}
                dot={false}
                isAnimationActive={false}
                name={eventBName}
              />
            )}
          </ComposedChart>
        </ResponsiveContainer>
      )}
    </div>
  );
};

export default CompareEventsChart;
